import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useTheme, setInstallSettings, setSuperadmin } from "@/lib/uiTheme";
import ForcePasswordChange from "@/components/ForcePasswordChange";
import ForceMfaLimited from "@/components/ForceMfaLimited";
import Login from "@/pages/Login";
import SuperadminLogin from "@/pages/SuperadminLogin";
import Dashboard from "@/pages/Dashboard";
import Home from "@/pages/Home";
import ModuleHome from "@/pages/ModuleHome";
import Users from "@/pages/core/Users";
import Appearance from "@/pages/core/Appearance";
import Persons from "@/pages/core/Persons";
import GdprSettings from "@/pages/core/GdprSettings";
import SecuritySettings from "@/pages/core/SecuritySettings";
import CustomFieldDefs from "@/pages/core/CustomFieldDefs";
import Audit from "@/pages/core/Audit";
import LicensingAudit from "@/pages/core/LicensingAudit";
import LicenseActivation from "@/pages/core/LicenseActivation";
import Notifications from "@/pages/core/Notifications";
import MyAccount from "@/pages/core/MyAccount";
import Licenses from "@/pages/core/Licenses";
import SmtpSettings from "@/pages/core/SmtpSettings";
import Access from "@/pages/core/Access";
import LicensedEmployers from "@/pages/core/LicensedEmployers";
import Clients from "@/pages/core/Clients";
import UpdatesStaging from "@/pages/core/UpdatesStaging";
import Employer from "@/pages/mdm/Employer";
import OrgStructure from "@/pages/mdm/OrgStructure";
import Employments from "@/pages/mdm/Employments";
import Signatures from "@/pages/mdm/Signatures";
import Holidays from "@/pages/mdm/Holidays";
import PersonnelCategories from "@/pages/mdm/PersonnelCategories";
import EducationLevels from "@/pages/mdm/EducationLevels";
import Functions from "@/pages/mdm/Functions";
import Nomenclatoare from "@/pages/mdm/Nomenclatoare";
import Campaigns from "@/pages/planco/Campaigns";
import Planning from "@/pages/planco/Planning";
import Delegations from "@/pages/planco/Delegations";
import Archive from "@/pages/planco/Archive";
import Approvals from "@/pages/planco/Approvals";
import CalendarView from "@/pages/planco/Calendar";
import Reports from "@/pages/planco/Reports";
import PlanningYear from "@/pages/planco/PlanningYear";
import EmployeeFiles from "@/pages/employee_files/EmployeeFiles";
import DocumentCategories from "@/pages/employee_files/DocumentCategories";
import ExpiringDocuments from "@/pages/employee_files/ExpiringDocuments";
import StatFunctii from "@/pages/stat_functii/StatFunctii";
import PostCategories from "@/pages/stat_functii/PostCategories";
import StatDidacticConfig from "@/pages/stat_didactic/Config";
import StatDidacticYears from "@/pages/stat_didactic/Years";
import StatDidacticYearParams from "@/pages/stat_didactic/YearParams";
import StatDidacticPrograms from "@/pages/stat_didactic/Programs";
import StatDidacticPlans from "@/pages/stat_didactic/Plans";
import StatDidacticPlanEditor from "@/pages/stat_didactic/PlanEditor";
import StatDidacticCohorts from "@/pages/stat_didactic/Cohorts";
import StatDidacticCohortEditor from "@/pages/stat_didactic/CohortEditor";
import StatDidacticAllocation from "@/pages/stat_didactic/Allocation";
import StatDidacticNota from "@/pages/stat_didactic/Nota";
import StatDidacticOrderAssign from "@/pages/stat_didactic/OrderAssign";
import StatDidacticTheses from "@/pages/stat_didactic/Theses";
import StatDidacticStaffDepartments from "@/pages/stat_didactic/StaffDepartments";
import StatDidacticStaffDepartment from "@/pages/stat_didactic/StaffDepartment";
import PhDepartments from "@/pages/stat_didactic/PhDepartments";
import PhDepartment from "@/pages/stat_didactic/PhDepartment";
import PhCandidates from "@/pages/stat_didactic/PhCandidates";
import PhParams from "@/pages/stat_didactic/PhParams";
import PhTime from "@/pages/stat_didactic/PhTime";
import PhPayroll from "@/pages/stat_didactic/PhPayroll";
import JobDescription from "@/pages/stat_functii/JobDescription";
import DutyLibrary from "@/pages/stat_functii/DutyLibrary";
import TimeentriesPage from "@/pages/timeentries/TimeentriesPage";
import { PAGE_ACCESS } from "@/lib/pageAccess";

function ActivationRequiredNotice() {
  const { logout } = useAuth();
  return (
    <div style={{ padding: 60, maxWidth: 560, margin: "0 auto", textAlign: "center" }} data-testid="activation-required-notice">
      <h2>Instalarea nu este activată</h2>
      <p style={{ color: "#475569" }}>Platforma poate fi folosită după ce Adminul activează licența. Contactați administratorul.</p>
      <button className="btn btn-secondary" onClick={() => logout().then(() => { window.location.href = "/login"; })}>Deconectare</button>
    </div>
  );
}

function Protected({ children, roles, page }) {
  const { user, activationRequired, hasRole, allowed, permissionsReady, permsLoaded } = useAuth();
  const loc = useLocation();
  const access = page ? PAGE_ACCESS[page] : { roles };
  if (user === null) return <div style={{padding:40}}>Se încarcă...</div>;
  if (!user) return <Navigate to="/login" replace />;
  // paginile decid butoanele după drepturi: așteptăm lista lor (altfel prima afișare le-ar vedea pe toate lipsă)
  if (!permsLoaded && !activationRequired) return <div style={{padding:40}}>Se încarcă...</div>;
  // Instalare de client neactivată: singura pagină permisă este activarea licenței (doar pentru Admin).
  if (activationRequired && !hasRole("superadmin")) {
    if (!hasRole("admin")) return <><ActivationRequiredNotice /><ForcePasswordChange /><ForceMfaLimited /></>;
    if (loc.pathname !== "/admin/license") return <Navigate to="/admin/license" replace />;
  }
  if ((access.roles || access.perm) && !allowed(access.roles, access.perm)) {
    // pagina se poate deschide și printr-un profil de acces: până sosesc permisiunile nu redirecționăm
    if (!permissionsReady) return <div style={{padding:40}}>Se încarcă...</div>;
    return <Navigate to="/" replace />;
  }
  return <><Layout>{children}</Layout><ForcePasswordChange /><ForceMfaLimited /></>;
}

/** Rădăcina: în aspectele Fiord și Purple, pagina de start cu plăci; în cel navy, Panoul de control (ca înainte). */
function Root() {
  const theme = useTheme();
  // Fiord nu are pagină de start cu plăci (nici buton Start): la rădăcină stă direct Panoul de control
  return theme === "purple" ? <Home/> : <Dashboard/>;
}

/** Aduce de la server aspectul implicit al instalării (și dacă utilizatorii pot alege) după autentificare. */
function ThemeSync() {
  const { user, hasRole } = useAuth();
  // Superadmin vede mereu aspectul navy (decis de Sasha, 2026-11-02), indiferent de implicitul instalării sau de alegerea individuală
  const isSuper = !!(user && user !== false && hasRole("superadmin"));
  useEffect(() => { setSuperadmin(isSuper); }, [isSuper]);
  // aspectul implicit al instalării, citit și înainte de autentificare (pagina de autentificare îl are de la prima vizită)
  useEffect(() => { api.get("/ui-settings/public").then(r => setInstallSettings(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (user && user !== false) api.get("/ui-settings").then(r => setInstallSettings(r.data)).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeSync />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/sa-login" element={<SuperadminLogin />} />
          <Route path="/" element={<Protected><Root/></Protected>} />
          <Route path="/panou" element={<Protected><Dashboard/></Protected>} />
          <Route path="/m/:key" element={<Protected><ModuleHome/></Protected>} />
          <Route path="/notifications" element={<Protected><Notifications/></Protected>} />
          <Route path="/my-account" element={<Protected><MyAccount/></Protected>} />
          {/* Core */}
          <Route path="/admin/users" element={<Protected page="/admin/users"><Users/></Protected>} />
          <Route path="/admin/persons" element={<Protected page="/admin/persons"><Persons/></Protected>} />
          <Route path="/admin/custom-fields" element={<Navigate to="/mdm/nomenclatoare?tab=campuri" replace />} />
          <Route path="/admin/security" element={<Protected page="/admin/security"><SecuritySettings/></Protected>} />
          <Route path="/admin/gdpr" element={<Protected page="/admin/gdpr"><GdprSettings/></Protected>} />
          <Route path="/admin/audit" element={<Protected page="/admin/audit"><Audit/></Protected>} />
          <Route path="/admin/license" element={<Protected page="/admin/license"><LicenseActivation/></Protected>} />
          <Route path="/admin/licensing-audit" element={<Protected roles={["superadmin"]}><LicensingAudit/></Protected>} />
          <Route path="/admin/licenses" element={<Protected roles={["superadmin"]}><Licenses/></Protected>} />
          <Route path="/admin/licensed-employers" element={<Protected roles={["superadmin"]}><LicensedEmployers/></Protected>} />
          <Route path="/admin/clients" element={<Protected roles={["superadmin"]}><Clients/></Protected>} />
          <Route path="/admin/updates" element={<Protected roles={["superadmin"]}><UpdatesStaging/></Protected>} />
          <Route path="/admin/appearance" element={<Protected page="/admin/appearance"><Appearance/></Protected>} />
          <Route path="/admin/smtp" element={<Protected page="/admin/smtp"><SmtpSettings/></Protected>} />
          <Route path="/admin/access" element={<Protected page="/admin/access"><Access/></Protected>} />
          {/* Configurare Angajator */}
          <Route path="/mdm/employer" element={<Protected page="/mdm/employer"><Employer/></Protected>} />
          <Route path="/mdm/structure" element={<Protected page="/mdm/structure"><OrgStructure/></Protected>} />
          <Route path="/mdm/employments" element={<Protected page="/mdm/employments"><Employments/></Protected>} />
          <Route path="/mdm/signatures" element={<Protected page="/mdm/signatures"><Signatures/></Protected>} />
          <Route path="/mdm/personnel-categories" element={<Navigate to="/mdm/nomenclatoare?tab=categorii" replace />} />
          <Route path="/mdm/nomenclatoare" element={<Protected page="/mdm/nomenclatoare"><Nomenclatoare/></Protected>} />
          <Route path="/mdm/functions" element={<Navigate to="/mdm/nomenclatoare?tab=functii" replace />} />
          <Route path="/mdm/education-levels" element={<Navigate to="/mdm/nomenclatoare?tab=studii" replace />} />
          {/* PlanCo - Programare concedii */}
          <Route path="/planco/campaigns" element={<Protected page="/planco/campaigns"><Campaigns/></Protected>} />
          <Route path="/planco/delegations" element={<Protected page="/planco/delegations"><Delegations/></Protected>} />
          <Route path="/planco/planning" element={<Protected page="/planco/planning"><Planning/></Protected>} />
          <Route path="/planco/approvals" element={<Protected page="/planco/approvals"><Approvals/></Protected>} />
          <Route path="/planco/calendar" element={<Protected page="/planco/calendar"><CalendarView/></Protected>} />
          <Route path="/planco/reports" element={<Protected page="/planco/reports"><Reports/></Protected>} />
          <Route path="/planco/archive" element={<Protected page="/planco/archive"><Archive/></Protected>} />
          <Route path="/planco/holidays" element={<Protected page="/planco/holidays"><Holidays/></Protected>} />
          <Route path="/planco/planning-year" element={<Protected page="/planco/planning-year"><PlanningYear/></Protected>} />
          {/* Dosar Electronic Angajat */}
          <Route path="/employee-files" element={<Protected page="/employee-files"><EmployeeFiles/></Protected>} />
          <Route path="/employee-files/categories" element={<Protected page="/employee-files/categories"><DocumentCategories/></Protected>} />
          <Route path="/employee-files/expiring" element={<Protected page="/employee-files/expiring"><ExpiringDocuments/></Protected>} />
          {/* Stat de Funcții */}
          <Route path="/stat-functii" element={<Protected page="/stat-functii"><StatFunctii/></Protected>} />
          <Route path="/stat-functii/atributii" element={<Protected page="/stat-functii/atributii"><DutyLibrary/></Protected>} />
          <Route path="/fisa-post/post/:id" element={<Protected page="/fisa-post"><JobDescription/></Protected>} />
          <Route path="/fisa-post/raport/:id" element={<Protected page="/fisa-post"><JobDescription/></Protected>} />
          <Route path="/stat-functii/categorii" element={<Protected page="/stat-functii/categorii"><PostCategories/></Protected>} />
          <Route path="/stat-didactic/ani" element={<Protected page="/stat-didactic/ani"><StatDidacticYears/></Protected>} />
          <Route path="/stat-didactic/ani/:id" element={<Protected page="/stat-didactic/ani"><StatDidacticYearParams/></Protected>} />
          <Route path="/stat-didactic/programe" element={<Protected page="/stat-didactic/programe"><StatDidacticPrograms/></Protected>} />
          <Route path="/stat-didactic/planuri" element={<Protected page="/stat-didactic/planuri"><StatDidacticPlans/></Protected>} />
          <Route path="/stat-didactic/planuri/:id" element={<Protected page="/stat-didactic/planuri"><StatDidacticPlanEditor/></Protected>} />
          <Route path="/stat-didactic/formatiuni" element={<Protected page="/stat-didactic/formatiuni"><StatDidacticCohorts/></Protected>} />
          <Route path="/stat-didactic/formatiuni/:id" element={<Protected page="/stat-didactic/formatiuni"><StatDidacticCohortEditor/></Protected>} />
          <Route path="/stat-didactic/alocare/:planId" element={<Protected page="/stat-didactic/planuri"><StatDidacticAllocation/></Protected>} />
          <Route path="/stat-didactic/nota" element={<Protected page="/stat-didactic/nota"><StatDidacticNota/></Protected>} />
          <Route path="/stat-didactic/nota/atribuire" element={<Protected page="/stat-didactic/nota/atribuire"><StatDidacticOrderAssign/></Protected>} />
          <Route path="/stat-didactic/nota/lucrari" element={<Protected page="/stat-didactic/nota/lucrari"><StatDidacticTheses/></Protected>} />
          <Route path="/stat-didactic/stat" element={<Protected page="/stat-didactic/stat"><StatDidacticStaffDepartments/></Protected>} />
          <Route path="/stat-didactic/stat/:departmentId" element={<Protected page="/stat-didactic/stat"><StatDidacticStaffDepartment/></Protected>} />
          <Route path="/stat-didactic/plata-ora" element={<Protected page="/stat-didactic/plata-ora"><PhDepartments/></Protected>} />
          <Route path="/stat-didactic/plata-ora/candidati" element={<Protected page="/stat-didactic/plata-ora"><PhCandidates/></Protected>} />
          <Route path="/stat-didactic/plata-ora/configurari" element={<Protected page="/stat-didactic/plata-ora/configurari"><PhParams/></Protected>} />
          <Route path="/stat-didactic/plata-ora/pontaj" element={<Protected page="/stat-didactic/plata-ora"><PhTime/></Protected>} />
          <Route path="/stat-didactic/plata-ora/salarizare" element={<Protected page="/stat-didactic/plata-ora/salarizare"><PhPayroll/></Protected>} />
          <Route path="/stat-didactic/plata-ora/:departmentId" element={<Protected page="/stat-didactic/plata-ora"><PhDepartment/></Protected>} />
          <Route path="/stat-didactic/setari" element={<Protected page="/stat-didactic/setari"><StatDidacticConfig/></Protected>} />
          {/* Pontaj Proiecte */}
          <Route path="/timeentries" element={<Protected page="/timeentries"><TimeentriesPage/></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
export default App;
